import type { FC } from "react";
import { useCallback, useMemo } from "react";
import { router } from "expo-router";
import {
  Accordion,
  Card,
  ChartContainer,
  ChartMentalBalance,
  computeRadiusByValue,
  getIndexes,
  getValue,
  RenderLabel,
  RenderMd,
  RenderValue,
} from "@ptsd/uikit";
import type { FallbackRender } from "@sentry/react";
import { useTranslation } from "react-i18next";
import { Pie, PieChart, Sector } from "recharts";
import type { PieSectorDataItem } from "recharts/types/polar/Pie";
import { View } from "tamagui";

import { api } from "~/utils/api";
import { isTRPCError } from "~/utils/is-trpc-error";
import { normalizePieData } from "~/utils/normalize";
import { ErrorBoundary } from "~/components/blocks";
import { OverviewCard, OverviewEmptyCard } from "./overview-common";

export const OverviewBalance: FC = () => {
  const { t } = useTranslation("overview");
  const fallback = useCallback<FallbackRender>((props) => {
    return <BalanceChartFallback {...props} />;
  }, []);

  return (
    <OverviewCard title={t("balance.title")}>
      <BalanceDetails />
      <ErrorBoundary fallback={fallback}>
        <BalanceChart />
      </ErrorBoundary>
    </OverviewCard>
  );
};

const BalanceChartFallback: FallbackRender = ({ error }) => {
  const { t } = useTranslation("overview");
  return (
    <OverviewEmptyCard
      text={t("balance.empty")}
      visible={isTRPCError(error) && error.data?.code === "PRECONDITION_FAILED"}
    />
  );
};

const BalanceDetails: FC = () => {
  const { t } = useTranslation("overview");
  const data = useMemo(() => {
    const details = t("balance.details", { returnObjects: true });
    return [{ id: "details", ...details }];
  }, [t]);

  return (
    <View width={"100%"}>
      <Accordion gap={"$2"} data={data}>
        {({ content }) => <RenderMd md={content} />}
      </Accordion>
    </View>
  );
};

const BalanceChart: FC = () => {
  const [spheres] = api.dashboard.getSpheresScale.useSuspenseQuery({
    filter: {},
  });

  const data = useMemo(() => {
    return normalizePieData(spheres.data, ChartMentalBalance.innerSectors);
  }, [spheres]);

  const handleLabelClick = useCallback((name: string) => {
    router.navigate("/metrics/" + name);
  }, []);

  return (
    <View width={"100%"}>
      <Card
        variant="gray"
        alignItems="center"
        backgroundColor={"$warm-snow-500"}
      >
        <ChartContainer
          width="100%"
          maxWidth={900}
          height={600}
          config={ChartMentalBalance.config}
        >
          {({ width, height, widthScale }) => {
            const activeIndex = getIndexes(data);
            const outerRadius = Math.floor(width / 6);
            const innerRadius = Math.floor(outerRadius / 8);

            const activeShapeLabel = (props: PieSectorDataItem) => (
              <RenderLabel
                layout={"lg"}
                data={data}
                domain={props.name}
                widthScale={widthScale}
                {...props}
                containerWidth={width}
                containerHeight={height}
                onLabelClick={handleLabelClick}
              />
            );

            const activeShapeChart = (props: PieSectorDataItem) => {
              const { cx, cy, midAngle, name } = props;

              const dynamicOuterRadius = computeRadiusByValue(
                getValue("domain", name, data)?.value,
                outerRadius,
              );

              return (
                <>
                  <Sector {...props} outerRadius={dynamicOuterRadius} />
                  <RenderValue
                    cx={cx ?? 0}
                    cy={cy ?? 0}
                    innerRadius={innerRadius}
                    midAngle={midAngle ?? 0}
                    domain={name}
                    value={getValue("domain", name, data)?.value}
                    dynamicOuterRadius={dynamicOuterRadius}
                  />
                </>
              );
            };

            return (
              <PieChart>
                <Pie
                  data={ChartMentalBalance.outerSectors}
                  dataKey={"slice"}
                  nameKey={"domain"}
                  innerRadius={innerRadius}
                  outerRadius={outerRadius}
                  cx="50%"
                  cy="50%"
                  strokeWidth={1}
                  stroke={"white"}
                  activeIndex={activeIndex}
                  activeShape={activeShapeLabel}
                  isAnimationActive={false}
                />
                <Pie
                  isAnimationActive={false}
                  activeIndex={activeIndex}
                  activeShape={activeShapeChart}
                  strokeWidth={1}
                  stroke={"rgba(255,255,255,0.3)"}
                  data={data}
                  dataKey={"slice"}
                  nameKey={"domain"}
                  innerRadius={innerRadius}
                  cx="50%"
                  cy="50%"
                />
              </PieChart>
            );
          }}
        </ChartContainer>
      </Card>
    </View>
  );
};
